<script setup>
/**
 * Renders a `document_list` block — published 公眾查閱 documents resolved
 * server-side into `data.documents` ({id, title, description, url, file_ext,
 * file_size}). A download list rather than a card grid: each row links straight
 * to the stored file (media.hktada.org in prod). The documents analog of
 * MediaListBlock.
 */
const { t } = useI18n();

defineProps({ data: { type: Object, required: true } });

const EXT_CLASS = {
  pdf:  "bg-accent-red/10 text-accent-red",
  doc:  "bg-accent-blue/10 text-accent-blue",
  docx: "bg-accent-blue/10 text-accent-blue",
  xls:  "bg-brand-green/10 text-brand-green",
  xlsx: "bg-brand-green/10 text-brand-green",
};

function extClass(doc) {
  return EXT_CLASS[doc.file_ext] || "bg-soft text-ink/60";
}

function humanSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
</script>

<template>
  <section class="section">
    <div class="container-x">
      <!-- Optional section heading -->
      <div v-if="data.heading" class="mb-10 md:mb-12 space-y-3">
        <p v-if="data.eyebrow" class="text-xs tracking-[0.3em] uppercase text-accent-blue font-semibold">{{ data.eyebrow }}</p>
        <h2 class="font-display text-3xl md:text-4xl">{{ data.heading }}</h2>
        <div class="rainbow-rule"></div>
      </div>

      <ul v-if="(data.documents || []).length" class="grid gap-3 list-none p-0 m-0">
        <li v-for="doc in data.documents" :key="doc.id">
          <a
            :href="doc.url"
            target="_blank"
            rel="noopener"
            download
            class="group flex items-center gap-4 rounded-2xl border border-line/60 bg-paper px-5 py-4 no-underline transition hover:border-brand-green/50 hover:shadow-sm"
          >
            <span
              class="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-[11px] font-bold uppercase tracking-wide"
              :class="extClass(doc)"
            >{{ doc.file_ext || "file" }}</span>
            <span class="min-w-0 flex-1">
              <span class="block font-medium text-ink leading-snug group-hover:text-brand-green transition-colors">{{ doc.title }}</span>
              <span v-if="doc.description || doc.file_size" class="mt-0.5 block text-xs text-ink/50">
                {{ doc.description }}<template v-if="doc.description && doc.file_size"> · </template>{{ humanSize(doc.file_size) }}
              </span>
            </span>
            <span
              class="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-line/60 text-ink/50 transition group-hover:border-brand-green group-hover:text-brand-green"
              aria-hidden="true"
            >
              <svg class="h-4 w-4" viewBox="0 0 20 20" fill="none">
                <path d="M10 3v10m0 0l-4-4m4 4l4-4M4 17h12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
          </a>
        </li>
      </ul>

      <p v-else class="text-sm text-ink/50">{{ t("documents.empty") }}</p>
    </div>
  </section>
</template>
